import type { AssetProfile } from '@akem/shared';
import {
    Box,
    Chip,
    Stack,
    Typography,
    useTheme
} from '@mui/material';
import React from 'react';
import type { WidgetProps } from '../types/index.js';

interface CustomerSegment {
  name: string;
  description: string;
  percentage?: string;
  trends?: string[];
  risks?: string[];
}

interface CustomerSegmentResearch {
  segments: CustomerSegment[];
  summary?: string;
}

export const CustomerSegmentsWidget: React.FC<WidgetProps> = ({ widget }) => {
  const theme = useTheme();
  const profile = widget.inputs.find((i) => i.id === 'profile')?.value as AssetProfile | undefined;
  const research = profile?.research?.customerSegments as CustomerSegmentResearch | undefined;

  if (!research || !research.segments || research.segments.length === 0) {
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          No customer segment data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', p: 2 }}>
      {research.summary && (
        <Typography variant="body2" color="text.secondary" paragraph>
          {research.summary}
        </Typography>
      )}
      <Stack spacing={2}>
        {research.segments.map((segment, idx) => (
          <Box key={idx} sx={{ borderBottom: idx < research.segments.length - 1 ? '1px solid' : 'none', borderColor: 'divider', pb: idx < research.segments.length - 1 ? 2 : 0 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {segment.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {segment.description}
                </Typography>
              </Box>
              {segment.percentage && (
                <Chip label={segment.percentage} size="small" color="primary" variant="outlined" />
              )}
            </Stack>
            
            {(segment.trends?.length ?? 0) > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" fontWeight="600" color="text.secondary" display="block" gutterBottom>
                  TRENDS
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {segment.trends!.map((trend, tIdx) => (
                    <Chip key={tIdx} label={trend} size="small" sx={{ fontSize: '0.75rem' }} />
                  ))}
                </Stack>
              </Box>
            )}

            {(segment.risks?.length ?? 0) > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" fontWeight="600" color="error.main" display="block" gutterBottom>
                  RISKS
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {segment.risks!.map((risk, rIdx) => (
                    <Chip 
                      key={rIdx} 
                      label={risk} 
                      size="small" 
                      color="error" 
                      variant="outlined" 
                      sx={{ fontSize: '0.75rem' }} 
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};
